'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { cn } from '../utils/cn';
import { useGetIntegrationsQuery } from '../hooks/use-gateway-queries';
import { PlusIcon } from './icons';
import { useRtmSearchAllAccounts, useUpdateUIPreferences } from '../stores/system-settings-store';

type McpSelectProps = {
  isOpen: boolean;
  onClose: () => void;
  onAddAccount?: () => void;
  selectedAccountId?: string | undefined;
  onSelectAccount?: (accountId: string) => void;
  anchorRef?: React.RefObject<HTMLElement>;
};

export const McpSelect = ({
  isOpen,
  onClose,
  onAddAccount,
  selectedAccountId,
  onSelectAccount,
  anchorRef,
}: McpSelectProps) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
  const { data: integrationsData } = useGetIntegrationsQuery();
  const rtmSearchAllAccounts = useRtmSearchAllAccounts();
  const updateUIPreferences = useUpdateUIPreferences();

  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 640
  );

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const dropdownWidth = isMobile ? 300 : 400;

  const integrations = React.useMemo(() => {
    return (
      integrationsData?.body?.integrations?.map(int => ({
        id: int.account_id,
        orgName: int.business_name,
        apiKey: int.api_key,
        secretKey: int.secret_key,
      })) || []
    );
  }, [integrationsData]);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: rect.right - dropdownWidth,
      });
    }
  }, [isOpen, anchorRef, dropdownWidth]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        anchorRef?.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !position) return null;

  const dropdown = (
    <div
      ref={dropdownRef}
      className={cn(
        'fixed p-2',
        'bg-popover border border-border rounded-lg shadow-lg',
        'animate-in fade-in-0 zoom-in-95 duration-200',
        'z-[9999]',
        'flex flex-col overflow-hidden'
      )}
      style={
        isMobile
          ? {
              width: `${dropdownWidth}px`,
              top: `${position.top}px`,
              left: '50%',
              transform: 'translateX(-50%) translateY(-100%)',
            }
          : {
              width: `${dropdownWidth}px`,
              top: `${position.top}px`,
              left: `${position.left}px`,
              transform: 'translateY(-100%)',
            }
      }
    >
      <div className="flex flex-row items-center justify-between px-2 pb-2 border-b border-border text-foreground font-medium">
        <p className="text-sm">Enter Account</p>
        {onAddAccount && (
          <button
            onClick={onAddAccount}
            className="flex items-center p-1 hover:bg-accent rounded transition-colors"
            aria-label="Add account"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {/* Integrations List */}
      <div
        className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground scrollbar-track-transparent pt-2"
        style={{ maxHeight: 160 }}
      >
        {integrations.length > 0 ? (
          <div className="space-y-1 px-1">
            {integrations.map(integration => (
              <button
                key={integration.id}
                onClick={() => {
                  // Toggle: deselect if already selected, otherwise select
                  if (selectedAccountId === integration.id) {
                    onSelectAccount?.(undefined as any);
                  } else {
                    onSelectAccount?.(integration.id);
                  }
                  onClose();
                }}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2.5 rounded-md',
                  'text-left text-sm text-foreground',
                  'hover:bg-accent transition-colors',
                  selectedAccountId === integration.id && 'bg-accent'
                )}
              >
                <span className="truncate">{integration.orgName}</span>
                {selectedAccountId === integration.id && (
                  <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No integrations configured
          </div>
        )}
      </div>
      {/* RTM multi-account search toggle */}
      <div className="border-t border-border mt-1 pt-2 px-1">
        <button
          onClick={() => updateUIPreferences({ rtmSearchAllAccounts: !rtmSearchAllAccounts })}
          className={cn(
            'w-full flex items-center justify-between px-2 py-2 rounded-md',
            'text-left hover:bg-accent transition-colors'
          )}
        >
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-foreground">Auto-switch account</span>
            <span className="text-[11px] text-muted-foreground leading-tight">
              Searches all practices, not just the selected one
            </span>
          </div>
          {/* Simple pill toggle */}
          <div
            className={cn(
              'relative flex-shrink-0 w-8 h-4 rounded-full transition-colors ml-3',
              rtmSearchAllAccounts ? 'bg-primary' : 'bg-muted'
            )}
          >
            <div
              className={cn(
                'absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform',
                rtmSearchAllAccounts ? 'translate-x-[18px]' : 'translate-x-0.5'
              )}
            />
          </div>
        </button>
      </div>
    </div>
  );

  return createPortal(dropdown, document.body);
};

McpSelect.displayName = 'McpSelect';
